import React, { useEffect, useState } from 'react';
import { Calendar, Send, Mail, MapPin, Phone, CheckCircle2, Loader2 } from 'lucide-react';
import { contactInfo } from '../mock';
import { useToast } from '../hooks/use-toast';
import { track } from '../lib/analytics';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://simplygoldsimplygold-backend.onrender.com').replace(/\/$/, '');

const fields = [
  { name: 'name', label: 'Name', type: 'text', required: true, autoComplete: 'name' },
  { name: 'email', label: 'Email Address', type: 'email', required: true, autoComplete: 'email' },
  { name: 'phone', label: 'Phone', type: 'tel', autoComplete: 'tel' },
  { name: 'website', label: 'Company Website', type: 'text', autoComplete: 'url' },
  { name: 'location', label: 'Location', type: 'text', autoComplete: 'address-level2' },
];

const selectFields = [
  { name: 'project_type', label: 'Project Type', options: ['Brand story', 'Commercial', 'Trades & construction', 'Pro sports', 'Documentary', 'Other'] },
  { name: 'package', label: 'Package Interest', options: ['Gold', 'Silver', 'Bronze', 'Custom / not sure'] },
  { name: 'budget', label: 'Budget', options: ['Under $5,000', '$5,000–$10,000', '$10,000–$20,000', '$20,000+', 'Not sure yet'] },
  { name: 'timeline', label: 'Ideal Timeline', options: ['As soon as possible', 'Within 1–2 months', 'Within 3–6 months', 'More than 6 months', 'Flexible'] },
];

const emptyForm = {
  name: '', email: '', phone: '', website: '', location: '', project_type: '',
  package: '', budget: '', timeline: '', message: '', website_confirm: '',
};

const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const selectPackage = (event) => {
      setForm((current) => ({ ...current, package: event.detail }));
    };
    window.addEventListener('select-package', selectPackage);
    return () => window.removeEventListener('select-package', selectPackage);
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing info', description: 'Please add your name, email and a short project message.' });
      return;
    }
    setLoading(true);
    const controller = new AbortController();
    // Render's free tier may need 50+ seconds to wake after inactivity. Give
    // that first request enough time to finish instead of showing a false
    // failure immediately before the service becomes ready.
    const timeout = setTimeout(() => controller.abort(), 75000);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
        signal: controller.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Something went wrong.');
      }
      setSent(true);
      setForm(emptyForm);
      track('contact_form_succeeded', { project_type: form.project_type, package: form.package });
      toast({ title: 'Message sent!', description: 'Thanks — we’ll be in touch shortly.' });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      track('contact_form_failed', { reason: err.name === 'AbortError' ? 'timeout' : 'request_error' });
      const description = err.name === 'AbortError'
        ? `The contact service did not respond. Please email ${contactInfo.email} directly.`
        : (err.message || `Please try again or email ${contactInfo.email} directly.`);
      toast({ title: 'Could not send', description });
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <p className="font-display tracking-[0.35em] text-[#c9a85f] text-sm uppercase mb-5">Get In Touch</p>
          <h2 className="font-display font-bold text-white text-4xl md:text-5xl uppercase leading-[1.05]">
            Let’s tell your story
          </h2>
          <p className="text-white/60 mt-5 leading-relaxed">
            Tell us a little about your project — or skip the form and book a discovery call.
          </p>

          <a
            href={contactInfo.calendly}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 mt-8 border border-[#c9a85f]/50 hover:bg-[#c9a85f] hover:text-black text-white font-display font-semibold px-6 py-3.5 rounded-md transition-colors"
          >
            <Calendar size={18} />
            Book A Meeting
          </a>

          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-3 text-white/70">
              <Mail size={18} className="text-[#c9a85f]" />
              <a href={`mailto:${contactInfo.email}`} className="text-sm hover:text-[#c9a85f] transition-colors">{contactInfo.email}</a>
            </div>
            <div className="flex items-center gap-3 text-white/70">
              <Phone size={18} className="text-[#c9a85f]" />
              <a href={`tel:${contactInfo.phoneHref}`} className="text-sm hover:text-[#c9a85f] transition-colors">{contactInfo.phone}</a>
            </div>
            <div className="flex items-center gap-3 text-white/70">
              <MapPin size={18} className="text-[#c9a85f]" />
              <span className="text-sm">{contactInfo.location}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" data-form-type="other" data-lpignore="true" className="lg:col-span-3 bg-[#111] border border-white/10 rounded-2xl p-7 md:p-9">
          <div className="grid sm:grid-cols-2 gap-5">
            {fields.map((f) => (
              <div key={f.name}>
                <label htmlFor={`contact-${f.name}`} className="block text-white/70 text-xs font-display font-semibold tracking-widest uppercase mb-2">
                  {f.label}{f.required && <span className="text-[#c9a85f]"> *</span>}
                </label>
                <input
                  id={`contact-${f.name}`}
                  type={f.type}
                  name={f.name}
                  value={form[f.name]}
                  onChange={handleChange}
                  autoComplete={f.autoComplete}
                  required={f.required}
                  maxLength={200}
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore
                  data-testid={`contact-input-${f.name}`}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-[#c9a85f] transition-colors"
                />
              </div>
            ))}
            {selectFields.map((f) => (
              <div key={f.name}>
                <label htmlFor={`contact-${f.name}`} className="block text-white/70 text-xs font-display font-semibold tracking-widest uppercase mb-2">{f.label}</label>
                <select
                  id={`contact-${f.name}`}
                  name={f.name}
                  value={form[f.name]}
                  onChange={handleChange}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-[#c9a85f] transition-colors"
                >
                  <option value="">Select an option</option>
                  {f.options.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
            <label htmlFor="contact-website-confirm">Leave this field empty</label>
            <input id="contact-website-confirm" name="website_confirm" value={form.website_confirm} onChange={handleChange} tabIndex={-1} autoComplete="off" />
          </div>
          <div className="mt-5">
            <label htmlFor="contact-message" className="block text-white/70 text-xs font-display font-semibold tracking-widest uppercase mb-2">Message <span className="text-[#c9a85f]">*</span></label>
            <textarea
              id="contact-message"
              name="message"
              rows={4}
              value={form.message}
              onChange={handleChange}
              autoComplete="off"
              data-lpignore="true"
              data-form-type="other"
              data-1p-ignore
              data-testid="contact-input-message"
              required
              minLength={10}
              maxLength={4000}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-[#c9a85f] transition-colors resize-none"
            />
          </div>
          <p className="mt-3 text-xs text-white/40">Required fields are marked with *. We use your details only to respond to your inquiry.</p>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-button"
            className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#c9a85f] hover:bg-[#d8b970] disabled:opacity-60 disabled:cursor-not-allowed text-black font-display font-semibold px-8 py-3.5 rounded-md transition-colors tracking-wide"
          >
            {loading ? <><Loader2 size={17} className="animate-spin" /> Sending…</> : sent ? <><CheckCircle2 size={18} /> Sent</> : <><Send size={17} /> Submit</>}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
